// ChatGPT Ads Measurement Pixel. The stub and init live in index.html so the
// landing click id (oppref) is captured before the app hydrates. Events fired
// here share an id with the server Conversions API call so OpenAI can dedupe them.

const CHATGPT_PIXEL_ID = '8PZFZ3SfvTGDJeH5mfqvoV';

type Oaiq = {
  (command: 'consent', granted: boolean): void;
  (command: 'init', config: { pixelId: string; user?: Record<string, string> }): void;
  (command: 'measure', eventName: string, data: Record<string, unknown>, options?: { event_id: string }): void;
};

export type ChatGPTAttribution = {
  chatgptEventId?: string;
  chatgptOppref?: string;
  chatgptObref?: string;
  chatgptSourceUrl?: string;
  chatgptConsent?: boolean;
};

const measuredEventIds = new Set<string>();

export function chatGPTConsentGranted() {
  if (typeof window === 'undefined') return false;
  let choice: string | null = null;
  try {
    choice = localStorage.getItem('radarbi_google_consent_v2');
  } catch {
    return false;
  }
  if (choice === 'denied') return false;
  if (choice === 'granted') return true;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (/^(Europe\/|Atlantic\/(Reykjavik|Canary|Madeira|Azores|Faroe))/.test(tz)) return false;
  } catch {
    return false;
  }
  return true;
}

function pixel(): Oaiq | null {
  if (typeof window === 'undefined') return null;
  const fn = (window as Window & { oaiq?: Oaiq }).oaiq;
  return typeof fn === 'function' ? fn : null;
}

function readCookie(name: string) {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  if (!match) return '';
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

async function sha256Hex(value: string) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

export function setChatGPTConsent(granted: boolean) {
  try {
    pixel()?.('consent', granted);
  } catch {
    /* ads measurement must never break the app */
  }
}

// Hashed identifiers improve matching after login. Raw email never leaves the browser.
export async function setChatGPTUser(identity: { email?: string | null; userId?: string | null }) {
  const q = pixel();
  if (!q || !crypto?.subtle || !chatGPTConsentGranted()) return;
  try {
    const user: Record<string, string> = {};
    const email = identity.email?.trim().toLowerCase() || '';
    if (email) user.email_sha256 = await sha256Hex(email);
    const userId = identity.userId?.trim() || '';
    if (userId) user.external_id_sha256 = await sha256Hex(userId);
    if (Object.keys(user).length === 0) return;
    q('init', { pixelId: CHATGPT_PIXEL_ID, user });
  } catch {
    /* ads measurement must never break the app */
  }
}

export function chatGPTAttribution(eventId?: string): ChatGPTAttribution {
  if (typeof window === 'undefined') return {};
  let oppref = '';
  try {
    oppref = sessionStorage.getItem('radarbi_oppref') || '';
  } catch {
    oppref = '';
  }
  if (!oppref) {
    oppref = new URLSearchParams(window.location.search).get('oppref') || readCookie('__oppref');
  }
  const consent = chatGPTConsentGranted();
  const obref = consent ? readCookie('__obref') : '';
  return {
    ...(eventId ? { chatgptEventId: eventId } : {}),
    ...(oppref ? { chatgptOppref: oppref } : {}),
    ...(obref ? { chatgptObref: obref } : {}),
    chatgptConsent: consent,
    chatgptSourceUrl: window.location.href,
  };
}

function measureOnce(eventName: string, data: Record<string, unknown>, eventId: string) {
  if (!eventId || !chatGPTConsentGranted()) return;
  const key = `${eventName}:${eventId}`;
  if (measuredEventIds.has(key)) return;
  measuredEventIds.add(key);
  try {
    pixel()?.('measure', eventName, data, { event_id: eventId });
  } catch {
    measuredEventIds.delete(key);
  }
}

export async function trackChatGPTRegistration(eventId: string, identity: { email?: string | null; userId?: string | null } = {}) {
  if (!eventId || !chatGPTConsentGranted()) return;
  await setChatGPTUser(identity);
  measureOnce('registration_completed', { type: 'customer_action' }, eventId);
}

export async function trackChatGPTSubscription(params: {
  eventId: string;
  planId: string;
  amountCents: number;
  currency: string;
  email?: string | null;
  userId?: string | null;
}) {
  if (!params.eventId || params.amountCents <= 0 || !chatGPTConsentGranted()) return;
  await setChatGPTUser({ email: params.email, userId: params.userId });
  measureOnce('subscription_created', {
    type: 'plan_enrollment',
    plan_id: params.planId,
    amount: Math.round(params.amountCents),
    currency: params.currency,
  }, params.eventId);
}
